import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Sparkles, MessageSquare, Calendar } from "lucide-react";

import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Content from "@/pages/Content";
import Startups from "@/pages/Startups";
import Investors from "@/pages/Investors";
import Partners from "@/pages/Partners";
import MediaKit from "@/pages/MediaKit";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Imprint from "@/pages/Imprint";
import SubmitStartup from "@/pages/SubmitStartup";
import RequestInvestorCall from "@/pages/RequestInvestorCall";
import PartnerApplication from "@/pages/PartnerApplication";
import StartupStatus from "@/pages/StartupStatus";

import { AuthProvider } from "@/contexts/AuthContext";
import RequireAdmin from "@/components/auth/RequireAdmin";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForceChange from "@/pages/admin/AdminForceChange";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStartupsList from "@/pages/admin/AdminStartupsList";
import AdminStartupDetail from "@/pages/admin/AdminStartupDetail";
import AdminInvestorsList from "@/pages/admin/AdminInvestorsList";
import AdminInvestorDetail from "@/pages/admin/AdminInvestorDetail";
import AdminContactsList from "@/pages/admin/AdminContactsList";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminMagicLink from "@/pages/admin/AdminMagicLink";

import { StartupPortalAuthProvider } from "@/contexts/StartupPortalAuthContext";
import RequireStartupSession from "@/components/portal/RequireStartupSession";
import StartupPortalLayout from "@/components/portal/StartupPortalLayout";
import PortalLogin from "@/pages/portal/startup/Login";
import PortalVerify from "@/pages/portal/startup/Verify";
import PortalDashboard from "@/pages/portal/startup/Dashboard";
import PortalProfile from "@/pages/portal/startup/Profile";
import PortalDecks from "@/pages/portal/startup/Decks";
import PortalTimeline from "@/pages/portal/startup/Timeline";
import PortalStubPage from "@/pages/portal/startup/StubPage";

import { InvestorPortalAuthProvider } from "@/contexts/InvestorPortalAuthContext";
import RequireInvestorSession from "@/components/portal/investor/RequireInvestorSession";
import InvestorPortalLayout from "@/components/portal/investor/InvestorPortalLayout";
import InvLogin from "@/pages/portal/investor/Login";
import InvVerify from "@/pages/portal/investor/Verify";
import InvDashboard from "@/pages/portal/investor/Dashboard";
import InvProfile from "@/pages/portal/investor/Profile";
import InvThesis from "@/pages/portal/investor/Thesis";
import InvTimeline from "@/pages/portal/investor/Timeline";
import InvSecurity from "@/pages/portal/investor/Security";
import InvReferral from "@/pages/portal/investor/Referral";
import InvStubPage from "@/pages/portal/investor/StubPage";

import SmoothScrollProvider from "@/components/motion/SmoothScrollProvider";
import PageTransition from "@/components/motion/PageTransition";
import MotionProvider from "@/components/motion/MotionProvider";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Wrap public pages with the editorial Layout. Admin uses its own layout.
const PublicLayout = () => (
  <Layout>
    <PageTransition>
      <Outlet />
    </PageTransition>
  </Layout>
);

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <MotionProvider>
        <AuthProvider>
          <StartupPortalAuthProvider>
          <InvestorPortalAuthProvider>
            <SmoothScrollProvider>
              <ScrollToTop />
              <Routes>
              {/* PUBLIC */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/content" element={<Content />} />
                <Route path="/startups" element={<Startups />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/media-kit" element={<MediaKit />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/submit-startup" element={<SubmitStartup />} />
                <Route path="/startup-status" element={<StartupStatus />} />
                <Route path="/request-investor-call" element={<RequestInvestorCall />} />
                <Route path="/partner-application" element={<PartnerApplication />} />
              </Route>

              {/* ADMIN — public auth screens */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />

              {/* ADMIN — protected */}
              <Route element={<RequireAdmin />}>
                <Route path="/admin/force-change" element={<AdminForceChange />} />
                <Route element={<AdminLayout />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/startups" element={<AdminStartupsList />} />
                  <Route path="/admin/startups/:id" element={<AdminStartupDetail />} />
                  <Route path="/admin/investors" element={<AdminInvestorsList />} />
                  <Route path="/admin/investors/:id" element={<AdminInvestorDetail />} />
                  <Route path="/admin/contacts" element={<AdminContactsList />} />
                  <Route path="/admin/audit" element={<AdminAuditLog />} />
                  <Route path="/admin/magic-link" element={<AdminMagicLink />} />
                </Route>
              </Route>

              {/* STARTUP PORTAL — public auth screens */}
              <Route path="/portal/startup/login" element={<PortalLogin />} />
              <Route path="/portal/startup/verify" element={<PortalVerify />} />

              {/* STARTUP PORTAL — protected */}
              <Route element={<RequireStartupSession />}>
                <Route element={<StartupPortalLayout />}>
                  <Route path="/portal/startup/dashboard" element={<PortalDashboard />} />
                  <Route path="/portal/startup/profile" element={<PortalProfile />} />
                  <Route path="/portal/startup/decks" element={<PortalDecks />} />
                  <Route path="/portal/startup/timeline" element={<PortalTimeline />} />
                  <Route
                    path="/portal/startup/matches"
                    element={<PortalStubPage section="matches" accent="sage" icon={Sparkles} />}
                  />
                  <Route
                    path="/portal/startup/messages"
                    element={<PortalStubPage section="messages" accent="lavender" icon={MessageSquare} />}
                  />
                  <Route
                    path="/portal/startup/bookings"
                    element={<PortalStubPage section="bookings" accent="sky" icon={Calendar} />}
                  />
                </Route>
              </Route>

              {/* INVESTOR PORTAL — public auth screens */}
              <Route path="/portal/investor/login" element={<InvLogin />} />
              <Route path="/portal/investor/verify" element={<InvVerify />} />

              {/* INVESTOR PORTAL — protected */}
              <Route element={<RequireInvestorSession />}>
                <Route element={<InvestorPortalLayout />}>
                  <Route path="/portal/investor/dashboard" element={<InvDashboard />} />
                  <Route path="/portal/investor/profile" element={<InvProfile />} />
                  <Route path="/portal/investor/thesis" element={<InvThesis />} />
                  <Route path="/portal/investor/timeline" element={<InvTimeline />} />
                  <Route path="/portal/investor/security" element={<InvSecurity />} />
                  <Route path="/portal/investor/referral" element={<InvReferral />} />
                  <Route
                    path="/portal/investor/matches"
                    element={<InvStubPage section="matches" accent="sage" icon={Sparkles} />}
                  />
                  <Route
                    path="/portal/investor/messages"
                    element={<InvStubPage section="messages" accent="lavender" icon={MessageSquare} />}
                  />
                  <Route
                    path="/portal/investor/bookings"
                    element={<InvStubPage section="bookings" accent="sky" icon={Calendar} />}
                  />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<PublicLayout />}>
                <Route index element={<Home />} />
              </Route>
            </Routes>
            <Toaster position="bottom-right" />
            </SmoothScrollProvider>
          </InvestorPortalAuthProvider>
          </StartupPortalAuthProvider>
        </AuthProvider>
        </MotionProvider>
      </BrowserRouter>
    </div>
  );
}
